<template>
  <div class="leaflet-map leaflet-map--map">
    <div ref="map" :id="'leaflet-map-' + _uid" style="width: 100%; height: 100%"></div>
    <!-- <div class="leaflet-map--map__message" v-if="message !== null">
      <span>{{ message }}</span>
    </div> -->
  </div>
</template>

<script>
import _ from "lodash";
import gmapsInit from "../../geocoder/gmaps";

export default {
  name: "GoogleMap",
  props: {
    zoom: {
      type: Number,
      default: 8
    },
    items: {
      type: Array,
      default: () => [],
    },
    selectedItem: {
      type: Object,
      default: null,
    },
    resize: {
      type: Boolean,
      default: false
    },
    panControl: {
      type: Boolean,
      default: true
    },
    zoomControl: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      map: null,
      /**
       * This property stores the Google maps markers for the related items. We’re
       * storing an object in the shape of { [itemId]: Marker }. We can access a
       * marker by a selected item’s id. Look at the watcher for highlighting.
       */
      markers: {},
      googleMapsInfowindow: null,
      pin: "/assets/img/leaflet/marker-icon-red.svg",
      // pin: "/assets/img/leaflet/marker-icon-fill-red-small.svg",
    };
  },

  async mounted() {
    try {
      await gmapsInit();
    } catch (error) {
      this.$helpers.logger('Failed to initialize Google maps', error);
    }

    this.renderMarkersOnMap()
  },

  watch: {
    selectedItem(newVal, oldVal) {
      const marker = this.markers[newVal.id];

      if (marker) {
        google.maps.event.trigger(marker, 'click');
      }
    }
  },

  methods: {
    renderMarkersOnMap () {
      if (!this.$refs.map) {
        this.$helpers.logger('Not rendering markers on map: failed to find Google map HTML element reference. Maybe Vue component is unmounted')

        return
      }

      if (this.items.length === 0) {
        this.$helpers.logger('Not rendering markers on map: no items available', {
          items: this.items,
        })

        return
      }

      // this.$helpers.logger("Rendering items on Google map", {
      //   items: this.items,
      // });

      this.resetMap();

      const mapOptions = {
        zoom: this.zoom,
        center: new google.maps.LatLng(48.140499, 11.53506),
        disableDefaultUI: true,
        panControl: this.panControl,
        zoomControl: this.zoomControl,
        mapTypeControl: false,
        streetViewControl: false,
        scrollwheel: false,
        mapTypeId: google.maps.MapTypeId.ROADMAP,

        // style gotten here https://snazzymaps.com/style/54/routexl
        styles: [
          {
              "featureType": "administrative",
              "elementType": "all",
              "stylers": [
                  {
                      "visibility": "on"
                  },
                  {
                      "saturation": -100
                  },
                  {
                      "lightness": 20
                  }
              ]
          },
          {
              "featureType": "road",
              "elementType": "all",
              "stylers": [
                  {
                      "visibility": "on"
                  },
                  {
                      "saturation": -100
                  },
                  {
                      "lightness": 40
                  }
              ]
          },
          {
              "featureType": "water",
              "elementType": "all",
              "stylers": [
                  {
                      "visibility": "on"
                  },
                  {
                      "saturation": -10
                  },
                  {
                      "lightness": 30
                  }
              ]
          },
          {
              "featureType": "landscape.man_made",
              "elementType": "all",
              "stylers": [
                  {
                      "visibility": "simplified"
                  },
                  {
                      "saturation": -60
                  },
                  {
                      "lightness": 10
                  }
              ]
          },
          {
              "featureType": "landscape.natural",
              "elementType": "all",
              "stylers": [
                  {
                      "visibility": "simplified"
                  },
                  {
                      "saturation": -60
                  },
                  {
                      "lightness": 60
                  }
              ]
          },
          {
              "featureType": "poi",
              "elementType": "all",
              "stylers": [
                  {
                      "visibility": "off"
                  },
                  {
                      "saturation": -100
                  },
                  {
                      "lightness": 60
                  }
              ]
          },
          {
              "featureType": "transit",
              "elementType": "all",
              "stylers": [
                  {
                      "visibility": "off"
                  },
                  {
                      "saturation": -100
                  },
                  {
                      "lightness": 60
                  }
              ]
          }
        ],
      };

      const googleMapLatLonBounds = new google.maps.LatLngBounds();
      this.map = new google.maps.Map(this.$refs.map, mapOptions);

      [].concat(this.items).forEach(item => {
        const position = new google.maps.LatLng(item.lat, item.lon);
        googleMapLatLonBounds.extend(position);

        const marker = new google.maps.Marker({
          map: this.map,
          icon: this.pin,
          position,
        });

        google.maps.event.addListener(marker, 'click', () => {
          this.selectItem(item)

          if (!this.googleMapsInfowindow) {
            this.googleMapsInfowindow = new google.maps.InfoWindow();
          }

          const { country, phone } = item
          const { street, housenumber, zipcode, city } = item.address

          const name = `${item.name}  <span class="c-ranking c-ranking--${item.ranking}"></span>`
          const streetWithHousenumber = !!street ? `${street} ${housenumber || ''}`.trim() : ''

          const content = `<a \
            href="https://maps.google.com?daddr=${street} ${housenumber}+${zipcode}+${city}+${country || ''}" \
            target="_blank"> \
              Route berechnen
            </a>`;

          // const linkToItemInTable = `<a href="#${item.id}">in Liste anzeigen</a>`;

          const address = [
            name,
            ...(!!streetWithHousenumber ? [streetWithHousenumber] : []),
            `${zipcode} ${city}`,
            ...(!!country ? [country] : []),
            ...(!!phone ? [`Tel: ${phone}`] : []),
            content,
            // linkToItemInTable
          ];

          this.googleMapsInfowindow.setContent(
            address.join('<br>')
          );

          this.googleMapsInfowindow.open(this.map, marker);
        });

        this.markers[item.id] = marker;
      })

      if (this.items.length === 1) {
        const zoomChangeBoundsListener = google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
          this.map.setZoom(this.zoom);
        });

        setTimeout(() => {
          google.maps.event.removeListener(zoomChangeBoundsListener)
        }, 1000);
      }

      this.map.setCenter(mapOptions.center);
      this.map.fitBounds(googleMapLatLonBounds);

    },

    resetMap () {
      this.map = null;
    },

    selectItem(item) {
      this.$emit('onSelectItem', item)
    },
  }
};
</script>
